/**
 * Typed client for the `orchestra dashboard` JSON API.
 *
 * The shapes below mirror the `Json` builders in `Orchestra/Dashboard.lean`; the two are kept
 * in step by hand, so a change on one side is a change on both. Every field the Lean side
 * emits is always present — optionals are flattened to `""` there rather than omitted — which
 * is why nothing here is declared optional.
 *
 * Authentication is a cookie the browser holds and sends on its own (see `auth.ts`); no call
 * in this file attaches a credential, and none should.
 */

/** Thrown when the server rejects a request for want of a valid session. */
export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "UnauthorizedError";
  }
}

/** Thrown for any other non-2xx response, carrying whatever the server explained. */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export type QueueStatus = "pending" | "running" | "done" | "failed" | "unfinished" | "cancelled";
export type TaskStatus = "running" | "completed" | "failed" | "unfinished" | "cancelled";
export type ConcertStatus = "running" | "done" | "failed" | "cancelled";
export type IssueStatus = "open" | "claimed" | "completed" | "abandoned";

export interface QueueEntry {
  id: string;
  status: QueueStatus;
  createdAt: string;
  priority: number;
  /** Null for a repository-independent entry, which runs in a scratch workspace. */
  upstream: string | null;
  fork: string | null;
  prompt: string;
  series: string | null;
  backend: string | null;
  model: string | null;
  /** The run this entry became, once claimed. */
  taskId: string | null;
  concertId: string | null;
  concertStepKey: string | null;
}

export interface TaskRecord {
  id: string;
  status: TaskStatus;
  createdAt: string;
  /** Null for a repository-independent run, which ran in a scratch workspace. */
  upstream: string | null;
  fork: string | null;
  prompt: string;
  series: string | null;
  backend: string | null;
  model: string | null;
  sessionId: string | null;
  continuesFrom: string | null;
  budgetUsd: number | null;
}

export interface ConcertRun {
  id: string;
  status: ConcertStatus;
  name: string | null;
  workflowFile: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface Overview {
  counts: {
    running: number;
    pending: number;
    failed: number;
    concerts: number;
    listeners: number;
    totalTasks: number;
    authFree: number;
    authTotal: number;
  };
  activeQueue: QueueEntry[];
  recentTasks: TaskRecord[];
  /**
   * Base URL of the configured taxis tracker, or `null` if there is none.
   *
   * Projects live in taxis, so the dashboard links out rather than rendering them. `null`
   * means there is nothing to link to and the destination is hidden entirely.
   */
  taxisUrl: string | null;
}

/**
 * The envelope every collection endpoint answers in.
 *
 * `total` counts what matched before `limit` and `offset` were applied, which is what lets a
 * caller say "50 of 812" without a second request.
 */
export interface Collection<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConcertDetail {
  concert: ConcertRun;
  steps: QueueEntry[];
}

/**
 * One dispatch ceiling and where it stands. `nextAllowedAt` answers *when* the window next has
 * room, not *whether* it has any now — `remaining` is what says that.
 */
export interface RateLimitStatus {
  /** The ceiling as a person would say it, e.g. `"5 per hour"`. */
  description: string;
  max: number;
  windowSeconds: number;
  used: number;
  remaining: number;
  nextAllowedAt: string | null;
}

export interface ListenerSummary {
  name: string;
  enabled: boolean;
  sourceType: string;
  intervalSeconds: number;
  lastCheckedAt: string | null;
  eventCount: number;
  /** Empty when the listener is not paced. */
  rateLimits: RateLimitStatus[];
}

export interface ActionConfig {
  mode: "fork" | "pr";
  upstream: string;
  fork: string;
  series: string | null;
  backend: string | null;
  model: string | null;
  workflowPath: string | null;
  priority: number;
  promptTemplate: string;
}

export interface ListenerDetail {
  name: string;
  enabled: boolean;
  intervalSeconds: number;
  lastCheckedAt: string | null;
  eventCount: number;
  sourceType: string;
  sourceDetail: string;
  /** Source-kind-specific extras, as `[label, value]` pairs. */
  sourceExtras: [string, string][];
  /** Empty when the listener is not paced. */
  rateLimits: RateLimitStatus[];
  /** The dispatches still inside the longest configured window, oldest first. */
  recentDispatches: string[];
  action: ActionConfig;
  recentEvents: string[];
}

/**
 * One structured event from a task's JSONL log. The agent backends are free to add event
 * types, so this is a discriminated union with an open fallback rather than a closed set.
 */
export interface LogEvent {
  type?: string;
  subtype?: string;
  event_type?: string;
  model?: string;
  session_id?: string;
  stdout?: string;
  stderr?: string;
  result?: string;
  num_turns?: number;
  duration_ms?: number;
  total_cost_usd?: number;
  item?: {
    type?: string;
    text?: string;
    name?: string;
    input?: Record<string, unknown>;
  };
  [key: string]: unknown;
}

export interface TaskDetail {
  id: string;
  /**
   * The run `log` was read under. A queue entry and the task it becomes carry separate ids, so
   * an entry addressed by its own id resolves to this one; `null` means the entry has no run —
   * still queued, or failed before it started — and so has no trace.
   */
  taskId: string | null;
  status: TaskStatus | QueueStatus;
  /** Null for a repository-independent run, which ran in a scratch workspace. */
  fork: string | null;
  createdAt: string;
  prompt: string;
  /** The trailing `logLimit` events of the run's log, oldest first. */
  log: LogEvent[];
  /** How many events the log holds in total. */
  logTotal: number;
  /** The tail size the server used. */
  logLimit: number;
  /** Whether `log` is a tail rather than the whole thing. */
  logTruncated: boolean;
}

export interface IssueCounts {
  open: number;
  claimed: number;
  completed: number;
  abandoned: number;
}

export interface ProjectSummary {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  defaultTarget: string | null;
  issueCount: number;
  counts: IssueCounts;
}

export interface IssueNode {
  id: string;
  title: string;
  status: IssueStatus;
  parentId: string | null;
  dependencies: string[];
  prCount: number;
  claimedBy: string | null;
  updatedAt: string;
}

export interface ProjectDetail {
  project: ProjectSummary;
  issues: IssueNode[];
}

export interface UsageLimit {
  kind: string;
  scope: string | null;
  percent: number;
  severity: "normal" | "warning" | "critical" | string;
  active: boolean;
  resetsAt: string | null;
}

/**
 * A limit a run walked into, rather than one a poll reported.
 *
 * Kept apart from `UsageLimit` because the two are known in different ways. A poll reads the
 * account's session and weekly windows and can see nothing else — not a limit scoped to one
 * model family, not a billing failure — so those are only ever learned by a run hitting them,
 * and they appear here and nowhere else.
 */
export interface UsageBlock {
  /** The family this covers. `null` closes the whole account. */
  model: string | null;
  reason: string;
  /** When it lifts, if anything reported or guessed a time. */
  until: string | null;
}

export interface AuthSource {
  label: string;
  backend: string;
  kind: "oauth" | "api-key";
  baseUrl: string | null;
  isDefault: boolean;
  pollable: boolean;
  /** Branch on this, not on `availableAt`: a blocked source may report no reset time. */
  state: "available" | "blocked";
  reason: string | null;
  availableAt: string | null;
  pressure: number;
  /** How fresh these numbers are. */
  polledAt: string | null;
  lastUsedAt: string | null;
  lastError: string | null;
  backoffUntil: string | null;
  limits: UsageLimit[];
  /**
   * Limits observed by a run and still in force.
   *
   * A source whose only block is scoped to one model family still reports `state: "available"`
   * — correctly, because the other families run — so this is the only place that block is
   * visible.
   */
  blocks: UsageBlock[];
}

export interface AuthBackend {
  name: string;
  defaultSource: string | null;
  sources: AuthSource[];
}

export interface AuthView {
  configError: string | null;
  backends: AuthBackend[];
}

/** What `POST /api/v1/queue/{id}/cancel` reports back: the work the id resolved to. */
export interface CancelResult {
  /** The queue entry that was cancelled. */
  id: string;
  /** The run it became, if it had started one. */
  taskId: string | null;
}

/**
 * One session or weekly limit window, rolled up from every poll that landed inside it.
 *
 * `peakPercent` is what the window consumed — utilisation only climbs inside a window — and
 * `percent` is where it last stood, so on a closed window the two agree and on the open one
 * they say how much of it is already gone.
 */
export interface UsageWindow {
  kind: string;
  /** Model family a scoped window applies to. */
  scope: string | null;
  /** The first poll that landed in this window, not the window's own start. */
  startedAt: string;
  updatedAt: string;
  resetsAt: string | null;
  peakPercent: number;
  percent: number;
  /** How many polls saw this window. One is a glimpse of it, not a measurement. */
  samples: number;
  /** Whether this is the window still filling: the newest of its series, not yet past its reset. */
  open: boolean;
}

export interface UsageHistorySource {
  label: string;
  backend: string;
  kind: "oauth" | "api-key";
  /** False for API-key sources, which have no subscription window to accumulate history in. */
  pollable: boolean;
  /** Session windows, oldest first — the order a graph is drawn in. */
  sessions: UsageWindow[];
  /** Weekly windows, account-wide and model-scoped alike, oldest first. */
  weeks: UsageWindow[];
  /** Any other kind upstream reported, so nothing recorded is unreachable. */
  other: UsageWindow[];
}

export interface UsageHistoryBackend {
  name: string;
  sources: UsageHistorySource[];
}

export interface UsageHistory {
  configError: string | null;
  backends: UsageHistoryBackend[];
}


/* ── Interactive sessions ───────────────────────────────────────────────────────────────── */

export type SessionStatus =
  | "starting"
  | "idle"
  | "running"
  /** Nothing running, conversation intact: posting a turn starts an agent again and resumes it. */
  | "dormant"
  | "ended"
  | "failed";

export interface SessionSummary {
  id: string;
  status: SessionStatus;
  createdAt: string;
  lastActivityAt: string;
  endedAt: string | null;
  upstream: string;
  fork: string;
  backend: string;
  model: string | null;
  turnCount: number;
  costUsd: number;
  /** The last seq in the transcript. A client that has read this far is current. */
  lastEventSeq: number;
  title: string | null;
  error: string | null;
}

export interface SessionDetail extends SessionSummary {
  budget: number;
  slot: number;
  agentSessionId: string | null;
  resumedFrom: string | null;
}

/**
 * One line of a transcript. `kind` says what happened and the rest of the fields depend on it,
 * which is why they are all optional here: narrowing on `kind` is the only safe way to read one.
 */
export interface TranscriptEvent {
  seq: number;
  occurredAt: string;
  kind: "user" | "agent" | "turnStarted" | "turnEnded" | "notice";
  /** `user`. */
  text?: string;
  /** `agent`: a stream event, the same shape `LogView` already renders. */
  event?: LogEvent;
  /** `turnStarted`, `turnEnded`. */
  turn?: number;
  /** `turnEnded`. */
  subtype?: string;
  costUsd?: number | null;
  durationSeconds?: number | null;
  /** `notice`. */
  level?: "info" | "warning" | "error";
  message?: string;
}

/** A page of a transcript. Not a `Collection`: a cursor is not an offset. */
export interface Transcript {
  items: TranscriptEvent[];
  /** How many events follow the cursor in total, before the window. */
  total: number;
  limit: number;
  /** The cursor this answered. */
  after: number;
}

/** What starting a session asks for. Only the two repositories are required. */
export interface SessionRequest {
  upstream: string;
  fork: string;
  backend?: string;
  model?: string;
  budget?: number;
  tools?: string[];
  systemPrompt?: string;
  resumeFrom?: string;
}

/**
 * Maps each endpoint to the payload it returns. Detail endpoints take a path component, so
 * they are spelled as template literal types — that is what makes `useLiveData("tasks/" + id)`
 * resolve to `TaskDetail` rather than to `unknown`.
 */
export interface Endpoints {
  overview: Overview;
  queue: Collection<QueueEntry>;
  concerts: Collection<ConcertRun>;
  listeners: Collection<ListenerSummary>;
  tasks: Collection<TaskRecord>;
  projects: Collection<ProjectSummary>;
  auth: AuthView;
  usage: UsageHistory;
  interactive: Collection<SessionSummary>;
}

export type DetailEndpoint =
  | `tasks/${string}`
  | `concerts/${string}`
  | `listeners/${string}`
  | `projects/${string}`
  | `interactive/${string}`;

export type Endpoint = keyof Endpoints | DetailEndpoint;

/** The payload `E` resolves to. */
export type PayloadOf<E extends Endpoint> = E extends keyof Endpoints
  ? Endpoints[E]
  : E extends `tasks/${string}`
    ? TaskDetail
    : E extends `concerts/${string}`
      ? ConcertDetail
      : E extends `listeners/${string}`
        ? ListenerDetail
        : E extends `projects/${string}`
          ? ProjectDetail
          : E extends `interactive/${string}`
            ? SessionDetail
            : never;

/** The version prefix every read lives under. See `docs/openapi.json`. */
export const API_VERSION = "v1";

/**
 * Build the URL for an endpoint.
 *
 * The path component of a detail endpoint is percent-encoded, and the server percent-decodes
 * it before use (`safeSegment` in `Orchestra/Dashboard.lean`) — so an id containing a slash
 * or a `..` arrives as an ordinary name that simply matches nothing, rather than as a path.
 */
function endpointUrl(kind: "api" | "sse", endpoint: string, query?: QueryParams): string {
  const slash = endpoint.indexOf("/");
  const path =
    slash === -1
      ? endpoint
      : `${endpoint.slice(0, slash)}/${encodeURIComponent(endpoint.slice(slash + 1))}`;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) search.set(key, String(value));
  }
  const suffix = search.toString();
  return `/${kind}/${API_VERSION}/${path}${suffix === "" ? "" : `?${suffix}`}`;
}

/** Query parameters the API accepts. See `docs/openapi.json` for which endpoint takes which. */
export interface QueryParams {
  limit?: number;
  offset?: number;
  /** RFC 3339. Only on collections ordered by time. */
  since?: string;
  /** Only on a task detail. */
  logLimit?: number;
  /** Only on usage history: how many windows to return per series (a kind and a model scope). */
  windows?: number;
}

export function apiUrl(endpoint: string, query?: QueryParams): string {
  return endpointUrl("api", endpoint, query);
}

export function sseUrl(endpoint: string, query?: QueryParams): string {
  return endpointUrl("sse", endpoint, query);
}

async function readError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "error" in body) {
      return String((body as { error: unknown }).error);
    }
  } catch {
    /* fall through to the status line */
  }
  return `request failed (${response.status})`;
}

/** GET a payload, translating a 401 into `UnauthorizedError` so callers can show the login. */
export async function fetchEndpoint<E extends Endpoint>(
  endpoint: E,
  query?: QueryParams,
): Promise<PayloadOf<E>> {
  const response = await fetch(apiUrl(endpoint, query), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (response.status === 401) throw new UnauthorizedError();
  if (!response.ok) throw new ApiError(response.status, await readError(response));
  return (await response.json()) as PayloadOf<E>;
}

async function postJson(path: string, body?: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/** Exchange the password for a session cookie. Resolves `false` if it was rejected. */
export async function login(password: string): Promise<boolean> {
  const response = await postJson("/api/login", { password });
  if (response.status === 401) return false;
  if (!response.ok) throw new ApiError(response.status, await readError(response));
  return true;
}

/** Drop the session, server-side and in the browser. */
export async function logout(): Promise<void> {
  await postJson("/api/logout");
}

/**
 * Ask the daemon to stop one running task, named by its queue entry id or by the id of the run
 * it became — the server resolves either.
 *
 * The only call in this file that changes anything, and it changes exactly one task: nothing
 * else the daemon is running is affected, and the queue is left alone, so what is pending starts
 * as slots free up. The URL is built here rather than by `apiUrl`, which percent-encodes
 * everything after the first slash as a single component and would swallow the `/cancel`.
 *
 * A `404` means no entry and no run carries that id; a `409` means it is not running, or the
 * daemon is not. Both are answers to show, not bugs to swallow.
 */
export async function cancelTask(id: string): Promise<CancelResult> {
  const path = `/api/${API_VERSION}/queue/${encodeURIComponent(id)}/cancel`;
  const response = await postJson(path, {});
  if (response.status === 401) throw new UnauthorizedError();
  if (!response.ok) throw new ApiError(response.status, await readError(response));
  return (await response.json()) as CancelResult;
}


/* ── Interactive sessions ───────────────────────────────────────────────────────────────── */

/**
 * The transcript's URLs are built here rather than by `apiUrl`, which percent-encodes everything
 * after the first slash as a single component and would swallow the `/events` — the same reason
 * `cancelTask` builds its own.
 */
function transcriptPath(kind: "api" | "sse", id: string, after: number): string {
  return `/${kind}/${API_VERSION}/interactive/${encodeURIComponent(id)}/events?after=${after}`;
}

export function transcriptUrl(id: string, after: number): string {
  return transcriptPath("api", id, after);
}

export function transcriptStreamUrl(id: string, after: number): string {
  return transcriptPath("sse", id, after);
}

async function sessionCall(path: string, method: string, body?: unknown): Promise<Response> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 401) throw new UnauthorizedError();
  if (!response.ok) throw new ApiError(response.status, await readError(response));
  return response;
}

/**
 * Start a session: the daemon clones the repository, mints a token, starts an MCP server and
 * launches an agent that stays up across turns.
 *
 * The one call in this file that starts an agent. A `409` means the daemon is unreachable, or is
 * already holding as many sessions as it is configured to; a `400` names a backend that cannot
 * host one. Both are answers to show.
 */
export async function startSession(request: SessionRequest): Promise<SessionDetail> {
  const path = `/api/${API_VERSION}/interactive`;
  return (await (await sessionCall(path, "POST", request)).json()) as SessionDetail;
}

/** Post a turn. Answers the seq it was written at, so a reader knows where it landed. */
export async function sendTurn(id: string, text: string): Promise<{ id: string; seq: number }> {
  const path = `/api/${API_VERSION}/interactive/${encodeURIComponent(id)}/messages`;
  return (await (await sessionCall(path, "POST", { text })).json()) as { id: string; seq: number };
}

/** Abandon the turn in flight. The session, the clone and the conversation all survive. */
export async function interruptSession(id: string): Promise<void> {
  const path = `/api/${API_VERSION}/interactive/${encodeURIComponent(id)}/interrupt`;
  await sessionCall(path, "POST", {});
}

/** End a session and release the clone slot, the MCP server and the process. */
export async function endSession(id: string): Promise<void> {
  const path = `/api/${API_VERSION}/interactive/${encodeURIComponent(id)}`;
  await sessionCall(path, "DELETE");
}

/** Whether the browser currently holds a valid session. */
export async function checkSession(): Promise<boolean> {
  const response = await fetch("/api/session", { credentials: "same-origin" });
  if (!response.ok) return false;
  const body = (await response.json()) as { authenticated?: boolean };
  return body.authenticated === true;
}
